import {
  FinalFee,
  FinalFeeType,
  FinalProduct,
  FinanceResponseData,
  Insurance,
  InsuranceType,
  OtherCharge,
} from '@upstart/harmony/lib/legos/financingTypes';
import { ProductCategory } from '@upstart/harmony/lib/legos/penTypes';
import { Condition } from '@upstart/harmony/lib/legos/constants';
import {
  FormElementEnum,
  FormElementType,
  RicMetadata,
  RicFormSchema,
} from '../containers/PencilEditor/RICForm/ricFormTypes';

/**
 * Backend's `financeResponseWithDefaults` pads response data to a schema's declared
 * capacities before the live RIC page renders it, and Chairman cannot call that, so
 * this is generated from the schema rather than fixed — a fixed fixture would show
 * the wrong number of rows, differently for every state.
 */

/** Mirrors backend's `semantic-validation.ts`, which owns these pairings. */
const FEE_CAPACITIES: [keyof RicMetadata, FinalFeeType][] = [
  ['numOtherFees', FinalFeeType.OTHER],
  ['numGovernmentFeesTax', FinalFeeType.GOVERNMENT_FEES_TAX],
];

const PRODUCT_CAPACITIES: [keyof RicMetadata, ProductCategory][] = [
  ['numGapProducts', ProductCategory.GAP],
  ['numServiceContracts', ProductCategory.SERVICE_CONTRACT],
  ['numSurfaceProtectionProducts', ProductCategory.SURFACE_PROTECTION_PRODUCT],
  ['numTheftDeterrentDevices', ProductCategory.THEFT_DETERRENT_DEVICE],
];

const INSURANCE_CAPACITIES: [keyof RicMetadata, InsuranceType][] = [
  ['numInsuranceCreditLife', InsuranceType.CREDIT_LIFE],
  ['numInsuranceCreditDisability', InsuranceType.CREDIT_DISABILITY],
];

const CHARGE_CAPACITIES: [
  (
    | 'otherNonTaxableCharges'
    | 'otherTaxableCharges'
    | 'otherDownPayments'
    | 'carDefects'
  ),
  keyof RicMetadata,
][] = [
  ['otherNonTaxableCharges', 'numOtherNonTaxableCharges'],
  ['otherTaxableCharges', 'numOtherTaxableCharges'],
  ['otherDownPayments', 'numOtherDownpaymentElements'],
  ['carDefects', 'numDefects'],
];

const BASE: FinanceResponseData = {
  purchasePrice: 30000,
  sellingPrice: 30000,
  displayPrice: 30000,
  msrpPrice: 32000,
  // Summed by most states' unpaid-balance line; unset, it renders negative.
  effectivePrice: 30000,
  accessoriesPrice: 500,
  taxAmountOverride: 2400,
  mileage: 12000,
  carBody: 'Sedan',
  carCondition: Condition.USED,
  carStock: 'STOCK-0000',
  contractNumber: 'PREVIEW-0000',
  apr: 7.99,
  sellRate: 7.99,
  buyRate: 6.99,
  months: 60,
  contractMonths: 60,
  monthly: 550,
  downPayment: 3000,
  deferredDownPayment: 500,
  financeAmount: 27000,
  effectiveFinanceAmount: 27000,
  taxAmount: 2400,
  taxPercent: 8,
  fees: 800,
  netTrade: 4000,
  firstPaymentDueDate: '2026-10-01',
  rightToCancelDays: 2,
  rightToCancelFee: 250,
  otherDownPaymentDescription: 'Placeholder deferred down payment',
  otherDownPaymentAmount: 500,
};

interface SchemaIndex {
  feeTypes: Set<FinalFeeType>;
  productCategories: Set<ProductCategory>;
  insuranceTypes: Set<InsuranceType>;
  chargeFields: Set<string>;
  summedFields: Set<string>;
}

const each = <T>(value: unknown, visit: (item: T) => void) => {
  if (Array.isArray(value)) {
    value.forEach(visit);
  }
};

// Runs on whatever JSON is in the editor, ahead of the preview's error boundaries,
// so every traversal is guarded.
const indexElements = (elements: unknown, index: SchemaIndex): void => {
  each<FormElementType>(elements, (element) => {
    if (typeof element !== 'object' || element === null) {
      return;
    }

    if ('sumValue' in element) {
      each<string>(element.sumValue?.responseDataFields, (field) =>
        index.summedFields.add(field),
      );
    }

    switch (element.type) {
      case FormElementEnum.FEE_INPUT:
      case FormElementEnum.FEE_TABLE:
        index.feeTypes.add(element.feeType);
        break;
      case FormElementEnum.FEE_DROPDOWN:
        each<FinalFeeType>(element.feeTypes, (feeType) =>
          index.feeTypes.add(feeType),
        );
        break;
      case FormElementEnum.PRODUCT_TABLE:
        index.productCategories.add(element.productCategory);
        break;
      case FormElementEnum.INSURANCE_TABLE:
        each<InsuranceType>(element.insuranceTypes, (insuranceType) =>
          index.insuranceTypes.add(insuranceType),
        );
        break;
      case FormElementEnum.CHARGE_TABLE:
        index.chargeFields.add(element.responseDataField);
        break;
      case FormElementEnum.SECTION:
        indexElements(element.sectionElements, index);
        break;
      case FormElementEnum.SUBSECTION:
        indexElements(element.subSectionElements, index);
        break;
      default:
        break;
    }
  });
};

const capacity = (
  metadata: RicMetadata | undefined,
  key: keyof RicMetadata,
) => {
  const value = metadata?.[key];
  return typeof value === 'number' ? value : undefined;
};

const rows = <T>(count: number, build: (row: number) => T): T[] =>
  Array.from({ length: count }, (_unused, row) => build(row + 1));

export const buildPlaceholderFinanceResponseData = (
  schema: RicFormSchema,
): FinanceResponseData => {
  const metadata =
    typeof schema?.metadata === 'object' ? schema.metadata : undefined;
  const index: SchemaIndex = {
    feeTypes: new Set(
      Array.isArray(metadata?.feeTypeList) ? metadata?.feeTypeList : [],
    ),
    productCategories: new Set(),
    insuranceTypes: new Set(),
    chargeFields: new Set(),
    summedFields: new Set(),
  };
  indexElements(schema.elements, index);

  const feeCapacities = new Map(
    FEE_CAPACITIES.map(([key, feeType]) => [feeType, capacity(metadata, key)]),
  );
  const finalizedFeeList: FinalFee[] = Array.from(index.feeTypes).flatMap(
    (feeType) =>
      rows(Math.max(feeCapacities.get(feeType) ?? 1, 1), (row) => ({
        type: feeType,
        value: 50 * row,
        description: `Placeholder ${feeType} ${row}`,
        paidTo: `Placeholder payee ${row}`,
      })),
  );

  const productCapacities = new Map(
    PRODUCT_CAPACITIES.map(([key, category]) => [
      category,
      capacity(metadata, key),
    ]),
  );
  const finalizedProductList: FinalProduct[] = Array.from(
    index.productCategories,
  ).flatMap((category) =>
    rows(Math.max(productCapacities.get(category) ?? 1, 1), (row) => ({
      category,
      value: 100 * row,
      description: `Placeholder ${category} ${row}`,
      paidTo: `Placeholder provider ${row}`,
      months: 48,
      miles: 60000,
    })),
  );

  const insuranceCapacities = new Map(
    INSURANCE_CAPACITIES.map(([key, insuranceType]) => [
      insuranceType,
      capacity(metadata, key),
    ]),
  );
  const insurances: Insurance[] = Array.from(index.insuranceTypes).flatMap(
    (insuranceType) =>
      rows(Math.max(insuranceCapacities.get(insuranceType) ?? 1, 1), (row) => ({
        type: insuranceType,
        insuranceCompanyName: `Placeholder Insurance Co ${row}`,
        premium: 100 * row,
        termMonths: 60,
      })),
  );

  const charges = CHARGE_CAPACITIES.reduce<Record<string, OtherCharge[]>>(
    (acc, [field, key]) => {
      const count =
        capacity(metadata, key) ?? (index.chargeFields.has(field) ? 1 : 0);
      if (count > 0) {
        acc[field] = rows(count, (row) => ({
          description: `Placeholder ${field} ${row}`,
          paidTo: `Placeholder payee ${row}`,
          amount: 25 * row,
        }));
      }
      return acc;
    },
    {},
  );

  // Any other summed field, so a balance line totals something.
  const summed = Array.from(index.summedFields).reduce<Record<string, number>>(
    (acc, field) => {
      if (!(field in BASE) && !field.includes('.')) {
        acc[field] = 250;
      }
      return acc;
    },
    {},
  );

  return {
    ...BASE,
    // Keyed by schema-supplied field names, which the type cannot express.
    ...(summed as FinanceResponseData),
    ...(charges as FinanceResponseData),
    tradeIns: rows(
      Math.max(capacity(metadata, 'numTradeIns') ?? 1, 1),
      (row) => ({
        id: `preview-trade-in-${row}`,
        year: { name: 2019 },
        make: { name: 'Placeholder Motors' },
        model: { name: 'Preview' },
        currentLienHolder: 'Placeholder Lienholder',
        tradeInAllowance: 9000,
        payOff: 5000,
        mileage: '78000',
      }),
    ),
    finalizedFeeList,
    finalizedProductList,
    insurances,
  };
};
